import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { useAuth } from '../../../contexts/AuthContext';

// Create a custom axios instance with the correct base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

const TourVehicleAssignment = ({ tourId, onUpdate }) => {
  const { user, isAuthenticated } = useAuth();
  // Get token from localStorage instead of expecting it from context
  const getToken = () => localStorage.getItem('token');
  
  // We'll set authorization headers on each request instead of using defaults
  // This ensures the token is always current and properly applied
  useEffect(() => {
    // Initial data fetch when component mounts and we have a tourId
    if (tourId) {
      fetchAssignedVehicles();
    }
  }, [tourId]);

  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  // This effect is now handled in the previous useEffect

  const fetchAssignedVehicles = async () => {
    const token = getToken();
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Using the correct API endpoint that exists in the backend
      // Include authorization header explicitly on each request
      const response = await api.get(`/api/v1/tours/${tourId}/vehicles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAssignedVehicles(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch assigned vehicles');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while fetching assigned vehicles');
      console.error('Error fetching assigned vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVehicles = async () => {
    const token = getToken();
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Using the correct API endpoint for vehicles
      // Include authorization header explicitly on each request
      const response = await api.get('/api/v1/vehicles', {
        params: { status: 'available' },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Debug the response structure
      console.log('Vehicles API response:', response.data);
      
      // Check if response has data and handle different response structures
      if (response.data && response.data.success) {
        // Get the vehicles data, handling different possible response structures
        let vehiclesData = [];
        
        if (Array.isArray(response.data.data)) {
          vehiclesData = response.data.data;
        } else if (response.data.data && typeof response.data.data === 'object') {
          // If data is an object with vehicles property
          if (Array.isArray(response.data.data.vehicles)) {
            vehiclesData = response.data.data.vehicles;
          } else if (Array.isArray(response.data.data.data)) {
            // Nested data structure
            vehiclesData = response.data.data.data;
          } else {
            // If it's some other object structure, try to extract values
            vehiclesData = Object.values(response.data.data).filter(item => typeof item === 'object');
          }
        } else if (Array.isArray(response.data)) {
          // Direct array response
          vehiclesData = response.data;
        }
        
        // Filter out already assigned vehicles if we have valid data
        if (Array.isArray(vehiclesData)) {
          const assignedIds = assignedVehicles.map(v => v.id);
          const available = vehiclesData.filter(v => v && v.id && !assignedIds.includes(v.id));
          setAvailableVehicles(available);
        } else {
          setError('Invalid vehicle data format received from server');
        }
      } else {
        setError(response.data?.message || 'Failed to fetch available vehicles');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while fetching available vehicles');
      console.error('Error fetching available vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    fetchAvailableVehicles();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVehicleId(null);
    setIsDefault(false);
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleAssignVehicle = async () => {
    if (!selectedVehicleId) return;
    
    const token = getToken();
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Using the correct API endpoint for assigning a vehicle to a tour
      // Include authorization header explicitly on each request
      const response = await api.post(
        `/api/v1/admin/tours/${tourId}/assign-vehicle`, 
        { 
          vehicle_id: selectedVehicleId,
          is_default: isDefault 
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        // If this is set as default, update other vehicles' default status
        if (isDefault) {
          setAssignedVehicles(prev => 
            prev.map(v => ({
              ...v,
              pivot: {
                ...v.pivot,
                is_default: v.id === selectedVehicleId
              }
            }))
          );
        }
        
        // Add the newly assigned vehicle to the list
        const newVehicle = availableVehicles.find(v => v.id === selectedVehicleId);
        if (newVehicle) {
          setAssignedVehicles(prev => [
            ...prev, 
            {
              ...newVehicle,
              pivot: {
                tour_id: parseInt(tourId),
                vehicle_id: selectedVehicleId,
                is_default: isDefault
              }
            }
          ]);
        }
        
        handleCloseDialog();
        if (onUpdate) onUpdate();
      } else {
        setError(response.data.message || 'Failed to assign vehicle');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while assigning vehicle');
      console.error('Error assigning vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVehicle = async (vehicleId) => {
    const token = getToken();
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Using the correct API endpoint for removing a vehicle from a tour
      // Include authorization header explicitly on each request
      const response = await api.delete(
        `/api/v1/admin/tours/${tourId}/remove-vehicle/${vehicleId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setAssignedVehicles(prev => prev.filter(v => v.id !== vehicleId));
        if (onUpdate) onUpdate();
      } else {
        setError(response.data.message || 'Failed to remove vehicle');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while removing vehicle');
      console.error('Error removing vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultVehicle = async (vehicleId) => {
    const token = getToken();
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Using the correct API endpoint for setting a default vehicle for a tour
      // Note: API uses PUT method according to the routes file
      // Include authorization header explicitly on each request
      const response = await api.put(
        `/api/v1/admin/tours/${tourId}/set-default-vehicle/${vehicleId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        // Update the default status in the UI
        setAssignedVehicles(prev => 
          prev.map(v => ({
            ...v,
            pivot: {
              ...v.pivot,
              is_default: v.id === vehicleId
            }
          }))
        );
        if (onUpdate) onUpdate();
      } else {
        setError(response.data.message || 'Failed to set default vehicle');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while setting default vehicle');
      console.error('Error setting default vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !openDialog) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Assigned Vehicles
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Assign Vehicle
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assignedVehicles.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No vehicles assigned to this tour yet.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Default</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedVehicles.map((vehicle) => (
                <TableRow key={vehicle.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {vehicle.image ? (
                        <Box
                          component="img"
                          sx={{ width: 50, height: 50, mr: 2, objectFit: 'cover', borderRadius: 1 }}
                          src={vehicle.image}
                          alt={vehicle.name}
                        />
                      ) : (
                        <DirectionsCarIcon sx={{ fontSize: 40, mr: 2, color: 'text.secondary' }} />
                      )}
                      <Box>
                        <Typography variant="subtitle2">{vehicle.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vehicle.registration_number}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{vehicle.type}</TableCell>
                  <TableCell>{vehicle.capacity}</TableCell>
                  <TableCell>
                    <Chip 
                      label={vehicle.status} 
                      color={vehicle.status === 'available' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {vehicle.pivot?.is_default ? (
                      <Chip 
                        icon={<StarIcon />} 
                        label="Default" 
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleSetDefaultVehicle(vehicle.id)}
                      >
                        Set as Default
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Remove vehicle from tour">
                      <IconButton 
                        color="error"
                        onClick={() => handleRemoveVehicle(vehicle.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vehicle Assignment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Assign Vehicle to Tour</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : availableVehicles.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No available vehicles to assign. All vehicles are either already assigned or not available.
            </Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {availableVehicles.map((vehicle) => (
                  <Grid item xs={12} md={6} key={vehicle.id}>
                    <Card 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        height: '100%',
                        border: selectedVehicleId === vehicle.id ? '2px solid #4caf50' : '1px solid #e0e0e0',
                        boxShadow: selectedVehicleId === vehicle.id ? '0 0 10px rgba(76, 175, 80, 0.3)' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSelectVehicle(vehicle.id)}
                    >
                      {selectedVehicleId === vehicle.id && (
                        <Chip 
                          icon={<CheckCircleIcon />} 
                          label="Selected" 
                          color="success"
                          sx={{ 
                            position: 'absolute', 
                            top: 10, 
                            right: 10, 
                            zIndex: 1 
                          }} 
                        />
                      )}
                      
                      {vehicle.image ? (
                        <Box
                          component="img"
                          sx={{ 
                            width: { xs: '100%', sm: 140 },
                            height: { xs: 140, sm: '100%' },
                            objectFit: 'cover'
                          }}
                          src={vehicle.image}
                          alt={vehicle.name}
                        />
                      ) : (
                        <Box
                          sx={{ 
                            width: { xs: '100%', sm: 140 },
                            height: { xs: 140, sm: '100%' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200'
                          }}
                        >
                          <DirectionsCarIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                        </Box>
                      )}
                      
                      <CardContent sx={{ flex: '1 0 auto' }}>
                        <Typography component="div" variant="h6">
                          {vehicle.name}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {vehicle.make} {vehicle.model} ({vehicle.model_year})
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Type:</Typography>
                          <Typography variant="body2">{vehicle.type}</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Capacity:</Typography>
                          <Typography variant="body2">{vehicle.capacity} passengers</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Registration:</Typography>
                          <Typography variant="body2">{vehicle.registration_number}</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Status:</Typography>
                          <Chip 
                            label={vehicle.status} 
                            color={vehicle.status === 'available' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Set as default vehicle for this tour"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleAssignVehicle} 
            variant="contained" 
            disabled={!selectedVehicleId || loading}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TourVehicleAssignment;
